import { GitMerge } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useDivergence } from "~/api/queries";
import { Button } from "~/foundation/components/Button";
import { Text } from "~/foundation/components/Text";

/**
 * W9 §A.2.1 — "this document has diverged." Hidden when the document has not diverged, or the
 * viewer cannot update it (`documentUpdate`). M2: a failed check renders nothing (kms's own
 * posture) but is never cached as a failure — `useDivergence` sets `gcTime: 0` so the badge
 * tries again on the next visit instead of staying silently stuck.
 */
export function DivergenceBadge({
  documentId,
  canUpdate,
  onMerge,
}: {
  documentId: string;
  canUpdate: boolean;
  onMerge: () => void;
}) {
  const { t } = useTranslation();
  const { data: divergence } = useDivergence(documentId, canUpdate);

  if (!canUpdate || !divergence?.divergent) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2">
      <GitMerge size={16} className="shrink-0 text-warning" />
      <Text variant="small" className="flex-1">
        {t("{{count}} contributors have diverged", { count: divergence.distinctAuthors })}
      </Text>
      <Button size="sm" variant="neutral" onClick={onMerge}>
        {t("Merge revisions")}
      </Button>
    </div>
  );
}
